import { FormEvent, ReactNode, useState } from "react";
import {
  getGetAccountAccessQueryKey,
  useGetAccountAccess,
  useRedeemAccountAccess,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AccountAccessGateProps = {
  children: ReactNode;
};

function errorMessage(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null &&
    "error" in error.data &&
    typeof error.data.error === "string"
  ) {
    return error.data.error;
  }
  return "That code could not be accepted. Check it and try again.";
}

export function AccountAccessGate({ children }: AccountAccessGateProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetAccountAccess();
  const redeem = useRedeemAccountAccess();
  const [code, setCode] = useState("");

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
        Checking account access…
      </div>
    );
  }

  if (data?.approved) return <>{children}</>;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode || redeem.isPending) return;
    redeem.mutate(
      { data: { code: trimmedCode } },
      {
        onSuccess: async () => {
          setCode("");
          await queryClient.invalidateQueries({
            queryKey: getGetAccountAccessQueryKey(),
          });
        },
      },
    );
  };

  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md border-border/70 shadow-sm">
        <CardHeader className="space-y-3">
          <p className="text-metadata text-primary">Private testing</p>
          <CardTitle className="font-serif text-2xl">Invite-only access</CardTitle>
          <CardDescription>
            ForgeBook is currently available by invitation. Enter the one-time
            access code provided by the account owner to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="account-access-code">Access code</Label>
              <Input
                id="account-access-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Enter your invitation code"
                autoComplete="one-time-code"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={256}
                disabled={redeem.isPending}
              />
            </div>
            {redeem.isError && (
              <p className="text-sm text-destructive" role="alert">
                {errorMessage(redeem.error)}
              </p>
            )}
            <Button className="w-full" type="submit" disabled={!code.trim() || redeem.isPending}>
              {redeem.isPending ? "Verifying…" : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}